import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const ClientRegistration = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agentInfo, setAgentInfo] = useState(null);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const messageRef = useRef(null);
  
  const [formData, setFormData] = useState({
    lead_traveler_first_name: '',
    lead_traveler_last_name: '',
    lead_traveler_email: '',
    lead_traveler_phone: '',
    lead_traveler_address: '',
    lead_traveler_dob: '',
    lead_traveler_passport_number: '',
    lead_traveler_passport_expiry: '',
    lead_traveler_passport_country: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    additional_members: []
  });

  const [newMember, setNewMember] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    date_of_birth: '',
    passport_number: '',
    passport_expiry: '',
    passport_country: ''
  });

  const showMessage = (msg, type = 'info') => {
    setMessage({ text: msg, type });
    
    // Scroll to top to ensure message is visible
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
    
    // Clear message after 20 seconds
    setTimeout(() => setMessage(''), 20000);
  };

  useEffect(() => {
    fetchAgentInfo();
  }, [token]);

  const fetchAgentInfo = async () => {
    try {
      const response = await fetch(`/api/client-registration/agent/${token}`);
      const data = await response.json();
      
      if (data.success) {
        setAgentInfo(data.data);
      } else {
        showMessage(data.message, 'error');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching agent info:', error);
      showMessage('Failed to load registration form', 'error');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleMemberChange = (field, value) => {
    setNewMember(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addMember = () => {
    if (newMember.first_name.trim() && newMember.last_name.trim()) {
      setFormData(prev => ({
        ...prev,
        additional_members: [...prev.additional_members, { ...newMember }]
      }));
      setNewMember({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        date_of_birth: '',
        passport_number: '',
        passport_expiry: '',
        passport_country: ''
      });
    }
  };

  const removeMember = (index) => {
    setFormData(prev => ({
      ...prev,
      additional_members: prev.additional_members.filter((_, i) => i !== index)
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Lead traveler validation
    if (!formData.lead_traveler_first_name.trim()) {
      newErrors.lead_traveler_first_name = 'First name is required';
    }
    
    if (!formData.lead_traveler_last_name.trim()) {
      newErrors.lead_traveler_last_name = 'Last name is required';
    }
    
    if (!formData.lead_traveler_email.trim()) {
      newErrors.lead_traveler_email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.lead_traveler_email)) {
      newErrors.lead_traveler_email = 'Please enter a valid email address';
    }
    
    if (!formData.lead_traveler_phone.trim()) {
      newErrors.lead_traveler_phone = 'Phone number is required';
    }
    
    // Optional fields - no validation required
    
    // Additional members - no validation for optional fields
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Prevent the mobile keyboard's "Go"/"Next"/"Return" key (or hitting Enter
  // on desktop) from submitting the form early while a client is still
  // filling out earlier fields. Without this, focusing any single-line
  // <input> and pressing Enter implicitly submits the whole form as soon as
  // the required fields (first/last name, email, phone - which are also the
  // first fields on the form) are filled, long before the client reaches
  // Address/DOB/Passport/Emergency Contact/Additional Travelers.
  const handleFormKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showMessage('Please correct the errors below and try again.', 'error');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await fetch(`/api/client-registration/register/${token}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('Registration completed successfully!', 'success');
        // Redirect to success page or show success message
      } else {
        // Handle backend validation errors
        if (data.errors && Array.isArray(data.errors)) {
          const backendErrors = {};
          const errorMessages = [];
          data.errors.forEach(error => {
            backendErrors[error.path] = error.msg;
            errorMessages.push(`${error.path}: ${error.msg}`);
          });
          setErrors(backendErrors);
          showMessage(`Please correct the following errors: ${errorMessages.join(', ')}`, 'error');
        } else {
          showMessage(data.message || 'Registration failed. Please check all required fields and try again.', 'error');
        }
      }
    } catch (error) {
      console.error('Registration error:', error);
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showMessage('Network error: Unable to connect to the server. Please check your internet connection and try again.', 'error');
      } else {
        showMessage(`Registration error: ${error.message || 'An unexpected error occurred'}. Please try again or contact support if the problem persists.`, 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading registration form...</p>
        </div>
      </div>
    );
  }

  if (!agentInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-800">Invalid registration link</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-blue-600 mb-2">
                Client Registration
              </h1>
              <p className="text-gray-600">
                Register with {agentInfo.agentName}
                {agentInfo.agencyName && ` from ${agentInfo.agencyName}`}
              </p>
            </div>
          </div>

          {message && (
            <div 
              ref={messageRef}
              className={`sticky top-4 z-50 p-4 rounded-md shadow-lg ${
                message.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
                message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
                'bg-blue-50 border border-blue-200 text-blue-800'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {message.type === 'error' && (
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                  {message.type === 'success' && (
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  )}
                  <span>{message.text}</span>
                </div>
                <button
                  onClick={() => setMessage('')}
                  className="ml-4 text-gray-400 hover:text-gray-600 focus:outline-none"
                  aria-label="Close message"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
            <div className="space-y-6">
              {/* Lead Traveler Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Lead Traveler Information</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      First Name *
                    </label>
                    <input
                      type="text"
                      value={formData.lead_traveler_first_name}
                      onChange={(e) => handleInputChange('lead_traveler_first_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.lead_traveler_first_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter first name"
                    />
                    {errors.lead_traveler_first_name && (
                      <p className="text-red-600 text-sm mt-1">{errors.lead_traveler_first_name}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      value={formData.lead_traveler_last_name}
                      onChange={(e) => handleInputChange('lead_traveler_last_name', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.lead_traveler_last_name ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter last name"
                    />
                    {errors.lead_traveler_last_name && (
                      <p className="text-red-600 text-sm mt-1">{errors.lead_traveler_last_name}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={formData.lead_traveler_email}
                      onChange={(e) => handleInputChange('lead_traveler_email', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.lead_traveler_email ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter email address"
                    />
                    {errors.lead_traveler_email && (
                      <p className="text-red-600 text-sm mt-1">{errors.lead_traveler_email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      value={formData.lead_traveler_phone}
                      onChange={(e) => handleInputChange('lead_traveler_phone', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.lead_traveler_phone ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter phone number"
                    />
                    {errors.lead_traveler_phone && (
                      <p className="text-red-600 text-sm mt-1">{errors.lead_traveler_phone}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address (Optional)
                  </label>
                  <textarea
                    value={formData.lead_traveler_address}
                    onChange={(e) => handleInputChange('lead_traveler_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter full address"
                    rows="3"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date of Birth (Optional)
                    </label>
                    <input
                      type="date"
                      value={formData.lead_traveler_dob}
                      onChange={(e) => handleInputChange('lead_traveler_dob', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.lead_traveler_dob ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.lead_traveler_dob && (
                      <p className="text-red-600 text-sm mt-1">{errors.lead_traveler_dob}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Passport Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.lead_traveler_passport_number}
                      onChange={(e) => handleInputChange('lead_traveler_passport_number', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter passport number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Passport Expiry (Optional)
                    </label>
                    <input
                      type="date"
                      value={formData.lead_traveler_passport_expiry}
                      onChange={(e) => handleInputChange('lead_traveler_passport_expiry', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        errors.lead_traveler_passport_expiry ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                    {errors.lead_traveler_passport_expiry && (
                      <p className="text-red-600 text-sm mt-1">{errors.lead_traveler_passport_expiry}</p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Passport Country (Optional)
                    </label>
                    <input
                      type="text"
                      value={formData.lead_traveler_passport_country}
                      onChange={(e) => handleInputChange('lead_traveler_passport_country', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter passport issuing country"
                    />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Emergency Contact (Optional)</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Contact Name
                    </label>
                    <input
                      type="text"
                      value={formData.emergency_contact_name}
                      onChange={(e) => handleInputChange('emergency_contact_name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter emergency contact name"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.emergency_contact_phone}
                      onChange={(e) => handleInputChange('emergency_contact_phone', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter emergency contact phone"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Relationship
                  </label>
                  <input
                    type="text"
                    value={formData.emergency_contact_relation}
                    onChange={(e) => handleInputChange('emergency_contact_relation', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Spouse, Parent, Sibling"
                  />
                </div>
              </div>

              {/* Additional Travelers */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">Additional Travelers (Optional)</h3>
                
                {/* List of added members */}
                {formData.additional_members.length > 0 && (
                  <div className="mb-6 space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Added Travelers:</h4>
                    {formData.additional_members.map((member, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                        <div>
                          <span className="font-medium">{member.first_name} {member.last_name}</span>
                          {member.email && <span className="text-gray-600 ml-2">({member.email})</span>}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeMember(index)}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add new member form */}
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Add New Traveler</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        First Name *
                      </label>
                      <input
                        type="text"
                        value={newMember.first_name}
                        onChange={(e) => handleMemberChange('first_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter first name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        value={newMember.last_name}
                        onChange={(e) => handleMemberChange('last_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter last name"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email (Optional)
                      </label>
                      <input
                        type="email"
                        value={newMember.email}
                        onChange={(e) => handleMemberChange('email', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors[`additional_members[${formData.additional_members.length}].email`] ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Enter email address"
                      />
                      {errors[`additional_members[${formData.additional_members.length}].email`] && (
                        <p className="text-red-600 text-sm mt-1">{errors[`additional_members[${formData.additional_members.length}].email`]}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone (Optional)
                      </label>
                      <input
                        type="tel"
                        value={newMember.phone}
                        onChange={(e) => handleMemberChange('phone', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter phone number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date of Birth (Optional)
                      </label>
                      <input
                        type="date"
                        value={newMember.date_of_birth}
                        onChange={(e) => handleMemberChange('date_of_birth', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Passport Number (Optional)
                      </label>
                      <input
                        type="text"
                        value={newMember.passport_number}
                        onChange={(e) => handleMemberChange('passport_number', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter passport number"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Passport Expiry (Optional)
                      </label>
                      <input
                        type="date"
                        value={newMember.passport_expiry}
                        onChange={(e) => handleMemberChange('passport_expiry', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Passport Country (Optional)
                      </label>
                      <input
                        type="text"
                        value={newMember.passport_country}
                        onChange={(e) => handleMemberChange('passport_country', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Enter passport country"
                      />
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={addMember}
                    disabled={!newMember.first_name.trim() || !newMember.last_name.trim()}
                    className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Add Traveler
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Registering...' : 'Complete Registration'}
                </button>
                
                <p className="text-sm text-gray-600 text-center mt-4">
                  By registering, you agree to share your information with your travel agent
                  to help plan your travel arrangements.
                  {formData.additional_members.length > 0 && (
                    <span className="block mt-1 font-medium">
                      Including {formData.additional_members.length} additional traveler{formData.additional_members.length > 1 ? 's' : ''}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientRegistration;
